import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectNewTuple,
  tryNewTuple,
} from "../../../store/slices/maintenances";
import {
  Typography,
  Grid,
  Button,
  Drawer,
  IconButton,
  TextField,
  Box,
  Stack,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useFormik } from "formik";
import ModalMessages from "../../layout/modal-messages";
import Spinner from "../../layout/spinner";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const validate = (values) => {
  const errors = {};

  if (!values.description) errors.description = "Requerido";
  if (!values.shortDesc) errors.shortDesc = "Requerido";

  return errors;
};

const TupleAdminDrawer = ({
  openDrawer,
  setOpenDrawer,
  description,
  shortDescription,
  id,
  setOpenDialog,
  typeTable,
  typeTableId,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  //Trae el estado de una fila nueva/editada/eliminada
  const newRow = useSelector(selectNewTuple);

  //Creo el formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      typeTableId: 0,
      key: 0,
      isDeleted: false,
      isActive: true,
      description: "",
      shortDesc: "",
    },
    validate,
    onSubmit: async (values) => {
      const action = await dispatch(tryNewTuple({ model: values }));
      if (action.payload.data.isValid) {
        setOpenDialog(true);
        setOpenDrawer(false);
      }
    },
  });

  //Actualizo el formik
  useEffect(() => {
    openDrawer &&
      formik.setValues({
        typeTableId: typeTableId,
        key: id,
        isDeleted: false,
        isActive: true,
        description: description,
        shortDesc: shortDescription,
      });
  }, [openDrawer]);

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={openDrawer}
      PaperProps={{
        sx: {
          zIndex: 1203,
          width: { xs: "100vw", sm: 500 },
          maxWidth: "100%",
          maxHeight: { xs: "90vh", sm: "100%" },
          borderRadius: { xs: "12px 12px 0 0", sm: 0 },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        },
      }}
      onClose={() => setOpenDrawer(false)}
      sx={{
        zIndex: 1203,
        flexShrink: 0,
      }}
    >
      {/* Encabezado Azul */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: "#194B6F",
          color: "white",
          position: "relative",
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 4 },
          pb: { xs: 1.5, sm: 3 },
        }}
      >
        <IconButton
          sx={{
            position: "absolute",
            top: { xs: 8, sm: 16 },
            right: { xs: 8, sm: 16 },
            color: "white",
          }}
          onClick={() => setOpenDrawer(false)}
        >
          <Close />
        </IconButton>

        <Typography
          align="left"
          sx={{
            fontWeight: 700,
            fontSize: 18,
            mb: { xs: 0, sm: 1 },
            pr: 4,
          }}
        >
          {formik.values.key == 0 ? "Agregar fila" : "Editar fila"}
        </Typography>

        <Typography
          variant="body2"
          align="left"
          sx={{ display: { xs: "none", sm: "block" } }}
        >
          Por favor, complete información en cada campo que se solicite en el
          formulario
        </Typography>
      </Box>

      {/* Contenido del Formulario */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          backgroundColor: "white",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Grid container rowSpacing={3} spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12}>
            <Typography
              align="left"
              color="primary"
              sx={{
                fontSize: "1.3em",
                fontWeight: "bold",
              }}
            >
              {typeTable}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="description"
              label="Descripción"
              value={formik.values.description}
              onChange={formik.handleChange}
              multiline
              rows={2}
              variant="outlined"
              fullWidth
              size="small"
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={
                formik.touched.description && formik.errors.description
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="shortDesc"
              label="Descripción Corta"
              value={formik.values.shortDesc}
              onChange={formik.handleChange}
              multiline
              rows={2}
              variant="outlined"
              fullWidth
              size="small"
              error={formik.touched.shortDesc && Boolean(formik.errors.shortDesc)}
              helperText={formik.touched.shortDesc && formik.errors.shortDesc}
            />
          </Grid>

          <Grid item xs={12} sx={{ textAlign: "center" }}>
            {isMobile ? (
              <Stack direction="column" spacing={2} sx={{ mt: 1, mb: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={formik.handleSubmit}
                  disabled={newRow?.isLoading}
                >
                  {newRow?.isLoading && <Spinner color="primary" />}
                  Guardar
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  disabled={newRow.isLoading}
                  onClick={() => setOpenDrawer(false)}
                >
                  Cancelar
                </Button>
              </Stack>
            ) : (
              <Box sx={{ mt: 2, mb: 2, display: "inline-block" }}>
                <Button
                  variant="contained"
                  onClick={formik.handleSubmit}
                  disabled={newRow?.isLoading}
                  sx={{ width: 100, marginRight: 2 }}
                >
                  {newRow?.isLoading && <Spinner color="primary" />}
                  Guardar
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={newRow.isLoading}
                  sx={{ border: 2, width: 100 }}
                  onClick={() => setOpenDrawer(false)}
                >
                  Cancelar
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      {newRow.errors && newRow.errors.length > 0 && (
        <ModalMessages errors={newRow.errors} />
      )}
    </Drawer>
  );
};

export default TupleAdminDrawer;